import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2 } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { SEO } from "@/components/site/SEO";
import { RevealSection } from "@/components/site/RevealSection";
import { AuroraGlow } from "@/components/site/AuroraGlow";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast";
import { PhoneField } from "@/components/site/PhoneField";
import { COUNTRY_CODES, DEFAULT_COUNTRY_ISO } from "@/i18n/countryCodes";

// Field entrance stagger — same fields, same order, just introduced one at
// a time instead of the whole form appearing as one flat block.
const fieldReveal = (i) => ({
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-10% 0px" },
  transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1], delay: i * 0.06 },
});

// HubSpot Forms API — portal 246971738, form "Agendar Llamada - Formulario de Contacto (Leads)".
// Public endpoint, CORS-enabled, no API key required (browser-safe).
const HUBSPOT_CONTACT_FORM_ENDPOINT =
  "https://api.hsforms.com/submissions/v3/integration/submit/246971738/12459eb1-49bc-4a04-aa5f-5764fa0214aa";

const TRACTION_LABELS = {
  yes: "Sí — tengo casos, clientes o resultados concretos",
  no: "Todavía no — estoy en etapa de idea",
};

const EMPTY_FORM = {
  fullName: "",
  email: "",
  phoneCountry: DEFAULT_COUNTRY_ISO,
  phoneNumber: "",
  company: "",
  whatBuild: "",
  frustration: "",
  traction: "",
  website: "",
};

const PHONE_DIGITS_RE = /[0-9]/g;

const inputClass =
  "rounded-none border-0 border-b border-borderline bg-transparent px-0 py-3 text-base text-ink placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:border-accent transition-colors duration-300";

function FormField({ label, error, children, testId }) {
  return (
    <div data-testid={`${testId}-field`}>
      <Label className="font-mono text-xs uppercase tracking-[0.15em] text-secondary">{label}</Label>
      <div className="mt-3">{children}</div>
      {error && (
        <p className="mt-2 text-xs text-accent" data-testid={`${testId}-error`}>
          {error}
        </p>
      )}
    </div>
  );
}

export default function Contact() {
  const { t, lang } = useLang();
  const contact = t.contact;
  const { toast } = useToast();

  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState("idle"); // idle | submitting | qualified | disqualified
  const [errors, setErrors] = useState({});

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const submitToHubSpot = async (data) => {
    // HubSpot is the only lead-capture channel now (no backend), so a
    // failed submission has to surface to the user instead of failing
    // silently — otherwise the lead is lost with no record anywhere.
    const res = await fetch(HUBSPOT_CONTACT_FORM_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        fields: [
          { name: "firstname", value: data.full_name },
          { name: "email", value: data.email },
          { name: "phone", value: data.phone },
          { name: "company", value: data.company },
          { name: "que_construyes_y_para_quien", value: data.what_build },
          {
            name: "cual_es_tu_mayor_frustracion_con_como_comunicas_tu_negocio_hoy",
            value: data.frustration,
          },
          {
            name: "tienes_resultados_clientes_o_trayectoria_real_que_mostrar",
            value: TRACTION_LABELS[data.has_traction] || data.has_traction,
          },
        ],
      }),
    });
    if (!res.ok) throw new Error(`HubSpot submit failed with status ${res.status}`);
  };

  const validate = () => {
    const errs = {};
    if (!form.fullName.trim()) errs.fullName = contact.form.required;
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) errs.email = contact.form.invalidEmail;
    const digits = (form.phoneNumber.match(PHONE_DIGITS_RE) || []).length;
    if (!form.phoneNumber.trim() || digits < 6) errs.phone = contact.form.invalidPhone;
    if (!form.company.trim()) errs.company = contact.form.required;
    if (!form.whatBuild.trim()) errs.whatBuild = contact.form.required;
    if (!form.frustration.trim()) errs.frustration = contact.form.required;
    if (!form.traction) errs.traction = contact.form.required;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const country = COUNTRY_CODES.find((c) => c.iso === form.phoneCountry) || COUNTRY_CODES[0];
    const fullPhone = `${country.dial} ${form.phoneNumber.trim()}`;

    setStatus("submitting");
    const payload = {
      full_name: form.fullName,
      email: form.email,
      phone: fullPhone,
      company: form.company,
      what_build: form.whatBuild,
      frustration: form.frustration,
      has_traction: form.traction,
      language: lang,
      website: form.website,
    };
    try {
      await submitToHubSpot(payload);
      // Qualification mirrors the site's own positioning: Malinois works
      // with businesses that already have traction, clients, or results
      // to show — that's exactly what this radio answer captures.
      setStatus(form.traction === "yes" ? "qualified" : "disqualified");
    } catch (err) {
      setStatus("idle");
      toast({ description: contact.errorMessage, variant: "destructive" });
    }
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setErrors({});
    setStatus("idle");
  };

  return (
    <div className="bg-paper text-ink pt-32 pb-24 min-h-screen">
      <SEO title={contact.meta.title} description={contact.meta.description} lang={lang} />

      {/* Two-column split waits for lg: (1024px) rather than md: (768px) —
          at tablet width a 5/7 split leaves the form column under 400px,
          too tight for the phone field's country-code + number pair. Phones
          and tablets both get the form at full comfortable width; only real
          desktop gets the editorial sticky-aside layout. */}
      <div className="max-w-6xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        {/* Sticky intro column — same editorial-spread pattern as the
            founder page (sticky aside + content), as a solid black panel —
            no aurora glow. On a box this size the glow's blobs read as a
            visible terracotta tint instead of the near-invisible warmth it
            adds to the small guide-download modal, so it's left out here
            to match that flat-black look. Every text color below is a
            paper tone; the default text-secondary/text-ink used elsewhere
            on this light page would be unreadable against bg-ink. */}
        <RevealSection
          className="bg-ink text-paper p-8 md:p-10 lg:col-span-5 lg:sticky lg:top-32 lg:self-start"
          as="div"
        >
          <div>
            <motion.span
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="block h-px w-12 bg-accent origin-left mb-6"
              aria-hidden="true"
            />
            <span className="font-mono text-xs md:text-sm tracking-[0.3em] text-accent">{contact.kicker}</span>
            <h1
              className="mt-6 font-heading italic text-3xl sm:text-4xl lg:text-5xl leading-tight text-paper [text-wrap:balance]"
              data-testid="contact-title"
            >
              {contact.title}
            </h1>
            <p className="mt-6 font-body text-base md:text-lg text-paper/75 leading-relaxed">{contact.intro}</p>
            <div className="mt-6 flex items-center gap-3">
              <span className="block w-8 h-px bg-paper/20" aria-hidden="true" />
              <p className="font-mono text-xs uppercase tracking-[0.15em] text-paper/60">{contact.introAttr}</p>
            </div>
          </div>
        </RevealSection>

        <RevealSection className="lg:col-span-7" as="div">
          <AnimatePresence mode="wait">
            {status === "qualified" || status === "disqualified" ? (
              <motion.div
                key="result"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="relative overflow-hidden border border-borderline p-8 md:p-12"
                data-testid="contact-result-panel"
              >
                {status === "qualified" && (
                  <div className="pointer-events-none absolute inset-0 -z-10" aria-hidden="true">
                    <AuroraGlow tone="brand-light" />
                  </div>
                )}
                <p
                  className="font-heading italic text-2xl md:text-3xl text-ink leading-relaxed [text-wrap:balance]"
                  data-testid="contact-result-message"
                >
                  {status === "qualified" ? contact.qualifiedMessage : contact.disqualifiedMessage}
                </p>

                {status === "qualified" && (
                  <div className="mt-10 border-t border-borderline pt-8" data-testid="contact-calendly-block">
                    <h3 className="font-heading italic text-lg text-ink">{contact.calendlyTitle}</h3>
                    <div className="mt-4 border border-dashed border-borderline p-8 text-center">
                      <p className="font-mono text-xs uppercase tracking-[0.15em] text-muted">
                        {contact.calendlyPlaceholder}
                      </p>
                    </div>
                  </div>
                )}

                <button
                  onClick={resetForm}
                  data-testid="contact-new-message-button"
                  className="mt-8 font-mono text-xs uppercase tracking-[0.15em] text-secondary hover:text-accent transition-colors duration-300 underline"
                >
                  {contact.newMessage}
                </button>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                onSubmit={handleSubmit}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-8"
                data-testid="contact-form"
              >
                <input
                  type="text"
                  name="website"
                  value={form.website}
                  onChange={update("website")}
                  autoComplete="off"
                  tabIndex={-1}
                  aria-hidden="true"
                  className="absolute -left-[9999px] w-px h-px opacity-0"
                />

                <motion.div {...fieldReveal(0)}>
                  <FormField label={contact.form.fullName} error={errors.fullName} testId="contact-fullname">
                    <Input
                      value={form.fullName}
                      onChange={update("fullName")}
                      data-testid="contact-fullname-input"
                      className={inputClass}
                    />
                  </FormField>
                </motion.div>

                <motion.div {...fieldReveal(1)}>
                  <FormField label={contact.form.email} error={errors.email} testId="contact-email">
                    <Input
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      data-testid="contact-email-input"
                      className={inputClass}
                    />
                  </FormField>
                </motion.div>

                <motion.div {...fieldReveal(2)}>
                  <FormField label={contact.form.phone} error={errors.phone} testId="contact-phone">
                    <PhoneField
                      countryIso={form.phoneCountry}
                      onCountryChange={(iso) => setForm((f) => ({ ...f, phoneCountry: iso }))}
                      number={form.phoneNumber}
                      onNumberChange={(v) => setForm((f) => ({ ...f, phoneNumber: v }))}
                      testId="contact-phone"
                      inputClass={inputClass}
                    />
                  </FormField>
                </motion.div>

                <motion.div {...fieldReveal(3)}>
                  <FormField label={contact.form.company} error={errors.company} testId="contact-company">
                    <Input
                      value={form.company}
                      onChange={update("company")}
                      data-testid="contact-company-input"
                      className={inputClass}
                    />
                  </FormField>
                </motion.div>

                <motion.div {...fieldReveal(4)}>
                  <FormField label={contact.form.whatBuild} error={errors.whatBuild} testId="contact-whatbuild">
                    <Textarea
                      value={form.whatBuild}
                      onChange={update("whatBuild")}
                      data-testid="contact-whatbuild-input"
                      className={`${inputClass} min-h-[100px]`}
                    />
                  </FormField>
                </motion.div>

                <motion.div {...fieldReveal(5)}>
                  <FormField label={contact.form.frustration} error={errors.frustration} testId="contact-frustration">
                    <Textarea
                      value={form.frustration}
                      onChange={update("frustration")}
                      data-testid="contact-frustration-input"
                      className={`${inputClass} min-h-[100px]`}
                    />
                  </FormField>
                </motion.div>

                <motion.div {...fieldReveal(6)}>
                  <Label className="font-mono text-xs uppercase tracking-[0.15em] text-secondary">
                    {contact.form.tractionLabel}
                  </Label>
                  <RadioGroup
                    value={form.traction}
                    onValueChange={(v) => setForm((f) => ({ ...f, traction: v }))}
                    className="mt-4 space-y-3"
                    data-testid="contact-traction-radio"
                  >
                    <label className="flex items-center gap-3 cursor-pointer font-body text-sm text-secondary">
                      <RadioGroupItem value="yes" data-testid="contact-traction-yes" />
                      {contact.form.tractionYes}
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer font-body text-sm text-secondary">
                      <RadioGroupItem value="no" data-testid="contact-traction-no" />
                      {contact.form.tractionNo}
                    </label>
                  </RadioGroup>
                  {errors.traction && (
                    <p className="mt-2 text-xs text-accent" data-testid="contact-traction-error">
                      {errors.traction}
                    </p>
                  )}
                </motion.div>

                <motion.div {...fieldReveal(7)}>
                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    data-testid="contact-submit-button"
                    className="inline-flex items-center gap-3 bg-accent text-paper px-10 py-5 text-sm uppercase tracking-[0.15em] font-mono hover:bg-accentHover transition-colors duration-300 disabled:opacity-60 focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-paper focus-visible:outline-none"
                  >
                    {status === "submitting" && <Loader2 size={16} className="animate-spin" />}
                    {status === "submitting" ? contact.form.submitting : contact.form.submit}
                  </button>
                </motion.div>
              </motion.form>
            )}
          </AnimatePresence>
        </RevealSection>
      </div>
    </div>
  );
}
